import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar
} from "recharts";
import { Users, DollarSign, Zap, TrendingUp, UserPlus, CreditCard, Clock, BarChart3 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ["#007AFF", "#00C805", "#FF9F0A", "#FF3B30", "#A78BFA", "#06B6D4"];

export default function AdminPage() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [totalUsers, setTotalUsers] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetch = async () => {
            try {
                const [statsRes, usersRes] = await Promise.all([
                    axios.get(`${API}/admin/stats`, { withCredentials: true }),
                    axios.get(`${API}/admin/users?limit=50`, { withCredentials: true }),
                ]);
                setStats(statsRes.data);
                setUsers(usersRes.data.users || []);
                setTotalUsers(usersRes.data.total || 0);
            } catch (e) {
                console.error("Admin fetch error:", e);
            }
            setLoading(false);
        };
        fetch();
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0A0A0A]">
                <Navbar />
                <div className="flex items-center justify-center py-32">
                    <div className="w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
                </div>
            </div>
        );
    }

    const s = stats || {};

    return (
        <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-page">
            <Navbar />
            <div className="max-w-7xl mx-auto px-6 py-8">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            Admin Dashboard
                        </h1>
                        <p className="text-sm text-zinc-500 mt-1">Monitor your app's usage and revenue</p>
                    </div>
                    <Badge className="bg-[#007AFF]/20 text-[#007AFF] border-[#007AFF]/30 rounded-full text-xs px-3 py-1">
                        Admin
                    </Badge>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <StatCard icon={Users} label="Total Users" value={s.total_users || 0} color="#007AFF" />
                    <StatCard icon={CreditCard} label="Active Subs" value={s.active_subscribers || 0} color="#00C805" />
                    <StatCard icon={DollarSign} label="Revenue" value={`$${(s.total_revenue || 0).toFixed(2)}`} color="#FF9F0A" />
                    <StatCard icon={Zap} label="Total Analyses" value={s.total_analyses || 0} color="#A78BFA" />
                </div>

                {/* Secondary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <StatCard icon={UserPlus} label="New (30d)" value={s.recent_signups_30d || 0} color="#06B6D4" />
                    <StatCard icon={Clock} label="On Trial" value={s.trial_users || 0} color="#007AFF" />
                    <StatCard icon={TrendingUp} label="Payments" value={s.total_payments || 0} color="#00C805" />
                    <StatCard icon={Users} label="Expired" value={s.expired_users || 0} color="#FF3B30" />
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    {/* Activity Timeline */}
                    <div className="md:col-span-2 border border-white/10 bg-[#121212] rounded-2xl p-6" data-testid="admin-activity-chart">
                        <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-4">Analysis Activity (14 days)</p>
                        <div className="h-52">
                            {(s.activity_timeline || []).length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={s.activity_timeline}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                                        <XAxis dataKey="date" stroke="rgba(255,255,255,0.2)" tick={{ fill: '#71717A', fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={d => d.slice(5)} />
                                        <YAxis stroke="rgba(255,255,255,0.2)" tick={{ fill: '#71717A', fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                                        <Tooltip contentStyle={{ background: '#121212', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', fontSize: '12px', color: '#fff' }} />
                                        <Bar dataKey="count" fill="#007AFF" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-full flex items-center justify-center text-zinc-600 text-sm">No activity data yet</div>
                            )}
                        </div>
                    </div>

                    {/* Top Categories */}
                    <div className="border border-white/10 bg-[#121212] rounded-2xl p-6" data-testid="admin-categories">
                        <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-4">Popular Categories</p>
                        {(s.top_categories || []).length > 0 ? (
                            <div className="h-40 flex items-center justify-center">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={s.top_categories} cx="50%" cy="50%" innerRadius={35} outerRadius={60} paddingAngle={3} dataKey="count" nameKey="category">
                                            {(s.top_categories || []).map((_, i) => (
                                                <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <div className="h-40 flex items-center justify-center text-zinc-600 text-sm">No data</div>
                        )}
                        <div className="flex flex-wrap gap-2 mt-3 justify-center">
                            {(s.top_categories || []).map((t, i) => (
                                <div key={t.category} className="flex items-center gap-1.5 text-[10px] text-zinc-400">
                                    <div className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                                    {t.category} ({t.count})
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Top Symbols */}
                {(s.top_symbols || []).length > 0 && (
                    <div className="border border-white/10 bg-[#121212] rounded-2xl p-6 mb-8" data-testid="admin-top-symbols">
                        <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-4">Most Analyzed Instruments</p>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            {(s.top_symbols || []).map((item, i) => (
                                <div key={item.symbol} className="bg-white/[0.03] rounded-xl p-3 flex items-center gap-3">
                                    <span className="text-xs text-zinc-600 font-mono">#{i + 1}</span>
                                    <div>
                                        <p className="text-sm font-bold text-white">{item.symbol}</p>
                                        <p className="text-[10px] text-zinc-500">{item.count} analyses</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Users Table */}
                <div className="border border-white/10 bg-[#121212] rounded-2xl p-6" data-testid="admin-users-table">
                    <div className="flex items-center justify-between mb-4">
                        <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500">Users ({totalUsers})</p>
                    </div>
                    <ScrollArea className="max-h-[400px]">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-white/10">
                                    <th className="text-left text-[10px] tracking-wider uppercase text-zinc-500 font-bold py-2 px-3">User</th>
                                    <th className="text-left text-[10px] tracking-wider uppercase text-zinc-500 font-bold py-2 px-3">Role</th>
                                    <th className="text-left text-[10px] tracking-wider uppercase text-zinc-500 font-bold py-2 px-3">Status</th>
                                    <th className="text-left text-[10px] tracking-wider uppercase text-zinc-500 font-bold py-2 px-3">Analyses</th>
                                    <th className="text-left text-[10px] tracking-wider uppercase text-zinc-500 font-bold py-2 px-3">Joined</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((u, i) => (
                                    <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                                        <td className="py-3 px-3">
                                            <p className="text-sm text-white font-medium">{u.name}</p>
                                            <p className="text-[10px] text-zinc-500">{u.email}</p>
                                        </td>
                                        <td className="py-3 px-3">
                                            <Badge variant="outline" className={`rounded-full text-[9px] px-2 py-0 ${u.role === 'admin' ? 'text-[#007AFF] border-[#007AFF]/30' : 'text-zinc-400 border-zinc-600'}`}>
                                                {u.role}
                                            </Badge>
                                        </td>
                                        <td className="py-3 px-3">
                                            <Badge variant="outline" className={`rounded-full text-[9px] px-2 py-0 ${u.subscription_status === 'active' ? 'text-[#00C805] border-[#00C805]/30' : u.subscription_status === 'trial' ? 'text-[#007AFF] border-[#007AFF]/30' : 'text-[#FF3B30] border-[#FF3B30]/30'}`}>
                                                {u.subscription_status || 'none'}
                                            </Badge>
                                        </td>
                                        <td className="py-3 px-3">
                                            <span className="text-sm font-mono text-white">{u.analysis_count || 0}</span>
                                        </td>
                                        <td className="py-3 px-3">
                                            <span className="text-xs text-zinc-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </ScrollArea>
                </div>
            </div>
        </div>
    );
}

function StatCard({ icon: Icon, label, value, color }) {
    return (
        <div className="border border-white/10 bg-[#121212] rounded-2xl p-5" data-testid={`admin-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4" style={{ color }} />
                <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-500">{label}</span>
            </div>
            <p className="text-2xl font-bold font-mono text-white">{value}</p>
        </div>
    );
}
